import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { isNil, has, isEmpty } from "lodash";

import { DateFieldType, DateFormats, FieldType } from "../constants/Date";

dayjs.extend(utc);
dayjs.extend(customParseFormat);

/**
 * @param {string | Date}  dateInput - A date string or date object
 * @param {string | null} [outputFormat = null] - An optional param for output format | DateFormats enum | Default is "MM/DD/YYYY h:mm a"
 * @param {string | null} [inputFormat = null] - An optional param for input format | DateFormats enum | Default is accepted formats by dayjs
 * @param {boolean | null} [isUTC = null] - An optional param if date to be formatted in UTC format only | Default is false
 *
 * @returns {string} This is the formatted date string or empty when invalid date object or string input is there
 */
export const formatDate = (
  dateInput: string | Date,
  outputFormat: string | null = null,
  inputFormat: string | null = null,
  isUTC: boolean | null = null
): string => {
  if (isNil(dateInput)) return "";

  const finalFormat = outputFormat || DateFormats.MM_DD_YYYY_h_mm_a;
  const base =
    dateInput instanceof Date || inputFormat
      ? dateInput
      : new Date(dateInput);

  const factory = isUTC ? dayjs.utc : dayjs;
  const dateObj = inputFormat
    ? factory(base, inputFormat)
    : factory(base as Date);

  return dateObj.isValid() ? dateObj.format(finalFormat) : "";
};

/**
 * @param {any}  restData - A restData any
 *
 * @returns {string} This is the utcdate
 */
export const utcDateToUser = (restData: any): any => {
  if (Array.isArray(restData)) {
    return restData.map(utcDateToUser);
  }

  if (restData && typeof restData === 'object') {
    Object.keys(restData).forEach((key) => {
      const val = restData[key];
      if (typeof val === 'string' && has(FieldType, key)) {
        handleDateConversion(restData, key, val);
      } else if (typeof val === 'object') {
        utcDateToUser(val);
      }
    });
  }

  return restData;
};

const handleDateConversion = (restData: any, key: string, val: string): void => {
  const parsed = dayjs.utc(val + (val.endsWith('Z') ? '' : 'Z'));
  if (parsed.isValid()) {
    restData[key] = parsed.toJSON();
  }
};


/**
 * @param {any}  restData -api request object
 * @param {string} DateFieldType - An param to change output format which don't have time on ui | DateFieldType enum"
 *
 * @returns {any} This is the final request object after formatted date
 */
export const userDate = (restData: any) => {
  if (Array.isArray(restData)) {
    for (const data of restData) {
      userDate(data);
    }
  } else if (typeof restData === "object") {
    for (const objectKey in restData) {
      if (has(DateFieldType, objectKey)) {
        if (restData[objectKey]) {
          restData[objectKey] = dayjs(restData[objectKey]).format("YYYY-MM-DD");
        }
      }
      userDate(restData[objectKey]);
    }
  }
  return restData;
};

/**following function used to return current date*/
export const currentDate = () => {
  return dayjs().format();
};
/**
 * @param {any}  date - a date string
 * @param {number} value - a value number
 * @param {any} type - type any
 *
 * @returns {any} following function used to add number of days/month in current provided date
 */
export const addDaysToDate = (date: string, value: number, type: any) => {
  return dayjs(date).add(value, type);
};

/**
 * @param {any}  date - a date string
 *
 * @returns {any} Function to check if the given UTC time is before or exactly at 11:59 PM
 */
export const isAfter11_59PM = (date: any) => {
  const dateTime = dayjs(date);
  const endOfDay = dateTime.endOf("day").subtract(1, "minute");
  const isEndOfDay = dayjs().isAfter(endOfDay, "minute");
  return isEndOfDay;
};
/**
 * @param {any}  date - a date string[] | null
 *
 * @returns {any} following function used to filter out latest date time from an array
 */
export const getLatestDate = (data: any) => {
  if (!isEmpty(data)) {
    const latestDateTime = data.reduce((latest: any, current: any) => {
      const latestDate = new Date(latest);
      const currentDate = new Date(current);
      return currentDate > latestDate ? current : latest;
    }, data[0]);
    return latestDateTime;
  }
  return "";
};
